import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import bookingAPI from '../services/bookingApi';
import { formatDateForAPI, isSunday } from '../utils/dateUtils';

const TimeSlotPage = () => {
  const navigate = useNavigate();
  const { selectedHall, selectedTimeSlots, toggleTimeSlot, selectDate } = useBooking();

  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
      dates.push({
        date,
        isToday: i === 0,
        displayText: date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        dateString: date.toDateString(),
        id: `date-${i}-${date.getTime()}`,
      });
    }
    return dates;
  }, []);

  useEffect(() => {
    if (!selectedHall) { navigate('/'); return; }
    selectDate(formatDateForAPI(selectedDate));
    loadTimeSlots();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHall, selectedDate]);

  const loadTimeSlots = async () => {
    try {
      setLoading(true);
      const formattedDate = formatDateForAPI(selectedDate);
      const res = await bookingAPI.getTimeSlots(selectedHall.id, formattedDate, null);
      if (!res.success) { alert(res.message || 'Failed to load time slots'); return; }
      const slots = (res.data || []).map(slot =>
        slot.reason?.includes('Early morning slots not available on Saturday')
          ? { ...slot, available: true, reason: null }
          : slot
      );
      setTimeSlots(slots);
    } catch (error) {
      if (window.confirm('Unable to load time slots. Retry?')) loadTimeSlots();
    } finally {
      setLoading(false);
    }
  };

  const isSlotAvailable = (slot) => slot.available !== false && slot.isAvailable !== false;
  const isSlotSelected = (slot) => selectedTimeSlots.some(s => s.id === slot.id);

  const handleToggle = (slot) => {
    if (!isSlotAvailable(slot)) return;
    const isFullDay = slot.time?.toLowerCase().includes('full day') || slot.id === '4';
    if (isFullDay) {
      selectedTimeSlots.forEach(s => { if (!s.time?.toLowerCase().includes('full day') && s.id !== '4') toggleTimeSlot(s); });
    } else {
      selectedTimeSlots.forEach(s => { if (s.time?.toLowerCase().includes('full day') || s.id === '4') toggleTimeSlot(s); });
    }
    toggleTimeSlot(slot);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    selectDate(formatDateForAPI(date));
    setShowDatePicker(false);
    selectedTimeSlots.forEach(s => toggleTimeSlot(s));
  };

  const handleContinue = () => {
    if (selectedTimeSlots.length === 0) { alert('Please select at least one time slot'); return; }
    navigate('/requirements');
  };

  const formatDisplayDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  if (!selectedHall) return null;

  return (
    <div style={s.page}>
      {/* Navbar */}
      <header style={s.navbar}>
        <div style={s.navInner}>
          <button style={s.backBtn} onClick={() => navigate('/home')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            Home
          </button>
          <div style={s.navLogoBox}>
            <img src="/logo.png" alt="Logo" style={s.navLogo} onError={e => { e.target.parentElement.style.display = 'none'; }} />
          </div>
        </div>
      </header>

      {/* Hall info bar */}
      <div style={s.hallBar}>
        <div style={s.hallBarInner}>
          <span style={s.hallBarLabel}>Booking for:</span>
          <span style={s.hallBarName}>{selectedHall.name}</span>
          {selectedHall.location && <span style={s.hallBarLocation}>— {selectedHall.location}</span>}
        </div>
      </div>

      <main style={{ ...s.main, padding: isMobile ? '20px 16px 120px' : '28px 24px 120px' }}>
        {/* Page heading */}
        <div style={s.heading}>
          <h1 style={s.headingTitle}>Select Date & Time</h1>
          <p style={s.headingSubtitle}>Choose your preferred date and available time slots</p>
        </div>

        {/* Date selector */}
        <div style={s.card}>
          <p style={s.cardLabel}>Date</p>
          <button style={s.dateBtn} onClick={() => setShowDatePicker(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span style={s.dateBtnText}>{formatDisplayDate(selectedDate)}</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <p style={s.dateHint}>You can book up to 30 days in advance</p>

          {isSunday(selectedDate) && (
            <div style={s.infoBanner}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span>Sunday bookings require a priority request. Contact admin for assistance.</span>
            </div>
          )}
        </div>

        {/* Time slots */}
        <div style={s.card}>
          <p style={s.cardLabel}>Available Time Slots</p>
          {loading ? (
            <div style={s.loadingRow}>
              <div style={s.spinner} />
              <span style={s.loadingText}>Loading slots...</span>
            </div>
          ) : (
            <div style={s.slotsGrid}>
              {timeSlots.map(slot => {
                const available = isSlotAvailable(slot);
                const selected = isSlotSelected(slot);
                return (
                  <div key={slot.id}>
                    <button
                      style={{
                        ...s.slotBtn,
                        ...(selected ? s.slotSelected : available ? s.slotAvailable : s.slotUnavailable),
                      }}
                      onClick={() => handleToggle(slot)}
                      disabled={!available}
                    >
                      <span style={s.slotTime}>{slot.time}</span>
                      <span style={{ ...s.slotDuration, color: selected ? 'rgba(255,255,255,0.8)' : '#6b7280' }}>
                        {slot.duration}
                      </span>
                    </button>
                    {!available && slot.reason && (
                      <p style={s.slotReason}>{slot.reason}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected summary */}
        {selectedTimeSlots.length > 0 && (
          <div style={s.summaryCard}>
            <p style={s.summaryLabel}>Selected ({selectedTimeSlots.length})</p>
            <div style={s.summaryPills}>
              {selectedTimeSlots.map(slot => (
                <span key={slot.id} style={s.pill}>{slot.time}</span>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Sticky footer */}
      <div style={s.footer}>
        <div style={s.footerInner}>
          <button style={s.footerBack} onClick={() => navigate('/home')}>Back</button>
          <button
            style={{ ...s.footerContinue, opacity: selectedTimeSlots.length === 0 ? 0.5 : 1, cursor: selectedTimeSlots.length === 0 ? 'not-allowed' : 'pointer' }}
            onClick={handleContinue}
            disabled={selectedTimeSlots.length === 0}
          >
            Continue {selectedTimeSlots.length > 0 && `(${selectedTimeSlots.length} selected)`}
          </button>
        </div>
      </div>

      {/* Date picker modal */}
      {showDatePicker && (
        <div style={s.overlay} onClick={() => setShowDatePicker(false)}>
          <div style={{ ...s.modal, width: isMobile ? '92%' : 460 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Select Date</h3>
              <button style={s.modalClose} onClick={() => setShowDatePicker(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div style={s.dateList}>
              {availableDates.map(d => {
                const isSelected = d.dateString === selectedDate.toDateString();
                return (
                  <button
                    key={d.id}
                    style={{ ...s.dateItem, ...(isSelected ? s.dateItemSelected : d.isToday ? s.dateItemToday : {}) }}
                    onClick={() => handleDateChange(d.date)}
                  >
                    <span style={{ ...s.dateItemText, ...(isSelected ? { color: '#fff', fontWeight: 700 } : d.isToday ? { color: '#4f46e5', fontWeight: 600 } : {}) }}>
                      {d.displayText} {d.isToday && '(Today)'}
                    </span>
                    {isSelected && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const s = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f9fafb',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    color: '#111827',
  },
  navbar: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navInner: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '0 20px',
    height: 60,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    color: '#4f46e5',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '6px 4px',
    fontFamily: 'inherit',
  },
  navLogoBox: { flexShrink: 0 },
  navLogo: { height: 44, width: 'auto', display: 'block' },

  hallBar: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
    padding: '10px 20px',
  },
  hallBarInner: {
    maxWidth: 900,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  hallBarLabel: { fontSize: 13, color: '#6b7280' },
  hallBarName: { fontSize: 14, fontWeight: 700, color: '#111827' },
  hallBarLocation: { fontSize: 13, color: '#6b7280' },

  main: {
    maxWidth: 900,
    margin: '0 auto',
  },
  heading: { marginBottom: 24 },
  headingTitle: { fontSize: 24, fontWeight: 700, color: '#111827', margin: '0 0 6px' },
  headingSubtitle: { fontSize: 14, color: '#6b7280', margin: 0 },

  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: '20px',
    marginBottom: 20,
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  cardLabel: { fontSize: 13, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 12px' },

  dateBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 10,
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    marginBottom: 8,
  },
  dateBtnText: { flex: 1, fontSize: 14, fontWeight: 600, color: '#111827' },
  dateHint: { fontSize: 12, color: '#9ca3af', margin: 0 },

  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '10px 12px',
    marginTop: 12,
    fontSize: 13,
    color: '#92400e',
    lineHeight: 1.5,
  },

  loadingRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '16px 0' },
  spinner: { width: 20, height: 20, border: '2px solid #e5e7eb', borderTop: '2px solid #4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  loadingText: { fontSize: 14, color: '#6b7280' },

  slotsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 10,
  },
  slotBtn: {
    width: '100%',
    padding: '14px 16px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 12,
    backgroundColor: '#fff',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  slotAvailable: {
    border: '1.5px solid #e5e7eb',
    backgroundColor: '#fff',
  },
  slotSelected: {
    border: '1.5px solid #4f46e5',
    backgroundColor: '#4f46e5',
  },
  slotUnavailable: {
    border: '1.5px solid #f3f4f6',
    backgroundColor: '#f9fafb',
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  slotTime: { fontSize: 14, fontWeight: 600, color: 'inherit' },
  slotDuration: { fontSize: 12 },
  slotReason: { fontSize: 11, color: '#dc2626', margin: '4px 0 0', paddingLeft: 4 },

  summaryCard: {
    backgroundColor: '#eff0fe',
    border: '1px solid #c7d2fe',
    borderRadius: 12,
    padding: '14px 18px',
    marginBottom: 20,
  },
  summaryLabel: { fontSize: 13, fontWeight: 700, color: '#4338ca', margin: '0 0 10px' },
  summaryPills: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  pill: { padding: '4px 12px', backgroundColor: '#4f46e5', color: '#fff', borderRadius: 20, fontSize: 12, fontWeight: 600 },

  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e7eb',
    zIndex: 99,
    boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
  },
  footerInner: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '14px 20px',
    display: 'flex',
    gap: 12,
  },
  footerBack: {
    padding: '11px 24px',
    border: '1.5px solid #e5e7eb',
    borderRadius: 10,
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  footerContinue: {
    flex: 1,
    padding: '11px 24px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#4f46e5',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
  },

  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 20,
    overflow: 'hidden',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 20px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 },
  modalClose: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' },
  dateList: { overflowY: 'auto', padding: '8px 12px' },
  dateItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 10px',
    border: 'none',
    borderBottom: '1px solid #f3f4f6',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    borderRadius: 8,
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  dateItemSelected: { backgroundColor: '#4f46e5', border: 'none', borderRadius: 10, margin: '2px 0' },
  dateItemToday: { backgroundColor: '#eff0fe', borderRadius: 10, margin: '2px 0' },
  dateItemText: { fontSize: 14, color: '#374151' },
};

export default TimeSlotPage;
